import React, { useState, useMemo, useRef } from 'react';
import RiskScoreTag from 'components/atoms/tags/riskScoreTag';
import { useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';
import classNames from 'classnames';
import { isUndefined } from 'lodash';
import {
  DownOutlined,
  InfoCircleFilled,
  EditOutlined,
} from '@ant-design/icons';
import BaseButton from 'components/atoms/baseButton';
import BaseSwitch from 'components/atoms/base-switch';
import UserAvatar from 'components/atoms/user-avatar';
import ContactAvailabilityViewForm from 'components/molecules/contact-availability/view-form';
import ContactAvailabilityUpdateForm from 'components/molecules/contact-availability/update-form';
import InformationTooltip from 'components/atoms/information-tooltip';
import MasterModal from 'components/organisms/masterModal';
import GeneralCommentModalNew from 'components/templates/rpm/patients/patient/generalComments/modal-new';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useSendComment } from 'infrastructure/hooks/comments/use-send';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { usePatientContactAvailability } from 'infrastructure/hooks/patient/use-patient-contact-availability';
import { convertTime } from 'infrastructure/functions';
import useUser from 'utils/useUser';

import { useActionButtons } from '../../hooks';
import PatientActionsDrawer from '../patient-actions-drawer';
import InteractiveTimeCard from '../interactive-time-card';
import Title from '../title';
import Badges from '../badges';
import PatientStatusRow from '../patient-status-row';
import PatientActionButtons from '../patient-action-buttons';
import s from './styles.module.scss';

import type { IContactAvailabilityUpdateFormRef } from 'components/molecules/contact-availability/update-form';
import type {
  DoNotCallRestrictionType,
  ModuleType,
} from 'infrastructure/enums';
import type {
  ICondition,
  ICreateComment,
  IPatientHoldingStatus,
} from 'infrastructure/interfaces';
import type { FC, ReactElement } from 'react';

interface CombinePatientBannerProps {
  patientGuid: string;
  showHoldingStatusBadge: boolean;
  showTemporaryHoldBadge?: boolean;
  isLoading: boolean;
  holdingStatus?: IPatientHoldingStatus;
  remainingDays: number;
  birthDate: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  rpmDoNotCall: boolean;
  ccmDoNotCall: boolean;
  rpmRestrictionType?: DoNotCallRestrictionType | null;
  ccmRestrictionType?: DoNotCallRestrictionType | null;
  showSalesForceIntegration?: boolean;
  isShowEmailTag?: boolean;
  pcmStatus?: boolean;
  sla?: number;
  providerServiceType?: string;
  serviceDeliveryModel?: string;
  physician?: ReactElement | string;
  referralTypes?: string;
  rpmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  ccmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  riskScore?: number;
  disabledCreateInternalAdmissionForms?: boolean;
  conditions?: ICondition[];
  careAgentName?: string;
  patientDetailsLoading?: boolean;
  conditionLoading?: boolean;
  showConsumablesBadge?: boolean;
  consumablesRemainingDays?: number;
  clinicName?: ReactElement | string;
  patientTZ?: string | null;
  ccmClinicTime?: number;
  ccmTotalTime?: number;
  rpmClinicTime?: number;
  rpmTotalTime?: number;
  clinicReviewTimeSeconds?: number;
  currentMonth?: string;
  mobileAppStatus?: boolean;
  pmpmStatus?: boolean;
  module?: ModuleType;
  clinicGuid?: string;
  emailSubscription?: boolean;
  apcmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  addLoading?: boolean;
  onAdd?: () => void;
}

const CombinePatientBanner: FC<CombinePatientBannerProps> = (props) => {
  const {
    patientGuid,
    showHoldingStatusBadge,
    showTemporaryHoldBadge,
    isLoading,
    holdingStatus,
    remainingDays,
    birthDate,
    fullName,
    firstName,
    lastName,
    rpmDoNotCall,
    ccmDoNotCall,
    rpmRestrictionType,
    ccmRestrictionType,
    showSalesForceIntegration,
    isShowEmailTag,
    pcmStatus,
    sla,
    providerServiceType,
    serviceDeliveryModel,
    physician,
    referralTypes,
    rpmStatusObj,
    ccmStatusObj,
    riskScore,
    conditions,
    careAgentName,
    ccmClinicTime = 0,
    ccmTotalTime = 0,
    rpmClinicTime = 0,
    rpmTotalTime = 0,
    clinicReviewTimeSeconds = 0,
    currentMonth,
    mobileAppStatus,
    conditionLoading,
    patientDetailsLoading,
    showConsumablesBadge,
    consumablesRemainingDays,
    disabledCreateInternalAdmissionForms,
    clinicName,
    patientTZ,
    pmpmStatus,
    apcmStatusObj,
    module,
    clinicGuid,
    emailSubscription,
    addLoading,
    onAdd,
  } = props;
  const { combinedActionDropdownButtons: _combinedActionDropdownButtons } =
    useActionButtons({
      patientGuid,
      disabledCreateInternalAdmissionForms,
    });
  const { t } = useTranslation();
  const { isReadOnlyRole } = useCheckRoleAccess();
  const { isCombinedModule, isBlockCcmModule, isBlockRpmModule, patientInfo } =
    useAccess({ module: module!, patientGuid });
  const { loading: commentLoading, sendComment } = useSendComment();
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [contactAvailabilityExpanded, setContactAvailabilityExpanded] =
    useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const sendCommentHandler = async (data: ICreateComment) => {
    const response = await sendComment(data);
    setIsCommentsOpen(false);
    return response;
  };
  const [isEdit, setIsEdit] = useState(false);
  const [anytime, setAnytime] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const updateFormRef = useRef<IContactAvailabilityUpdateFormRef>(null);

  // const { _isReadOnlyRole } = useCheckRoleAccess();
  const { isAgencyDoctor } = useUser();
  const preparedPatientTZ =
    patientTZ && patientTZ.split(' ')[0]?.replace(/[()]/g, '');

  const { availabilities } = usePatientContactAvailability({
    patientGuid,
  });

  const todayAvailability = useMemo(() => {
    const today = new Date().getDay();
    const todaySlots = availabilities.filter((a) => a.day === today);

    if (availabilities.length === 0) {
      return t('labels.anytime');
    }

    if (todaySlots.length === 0) {
      return t('labels.notAvailable');
    }

    if (todaySlots.length === 1) {
      return `${convertTime(todaySlots[0].startTime)} - ${convertTime(
        todaySlots[0].endTime,
      )}`;
    }

    return todaySlots
      .map(
        (slot) =>
          `${convertTime(slot.startTime)} - ${convertTime(slot.endTime)}`,
      )
      .join(', ');
  }, [availabilities, t]);

  React.useEffect(() => {
    setAnytime(!availabilities.length);
  }, [availabilities.length]);

  const conditionsText = conditions
    ?.map((c) => c.icdCode?.code)
    .filter(Boolean)
    .join('; ');

  const onEdit = () => {
    setIsEdit(true);
  };

  const onCancel = () => {
    setIsEdit(false);
    setAnytime(!availabilities.length);
  };

  const onSaved = () => {
    setIsEdit(false);
  };

  const onSave = async () => {
    if (updateFormRef.current) {
      setIsSaving(true);
      await updateFormRef.current.save().finally(() => {
        setIsSaving(false);
      });
    }
  };

  const contactAvailabilitySwitch = (
    <BaseSwitch
      disabled={isSaving}
      label={t('labels.anytime')}
      value={anytime}
      onChange={setAnytime}
    />
  );

  return (
    <div
      data-cy="patient-header"
      className={classNames(s.wrapper, {
        [s.corner]: showHoldingStatusBadge || showTemporaryHoldBadge,
      })}
      id="patient-banner"
    >
      {patientDetailsLoading || conditionLoading || isLoading ? (
        <div className={s['loading-box']}>
          <BaseLoader loading={isLoading} />
        </div>
      ) : (
        <>
          <Badges
            showPmpmBadge={pmpmStatus && isAgencyDoctor}
            remainingDays={remainingDays}
            showHoldingStatusBadge={showHoldingStatusBadge}
            holdingStatus={holdingStatus}
            showTemporaryHoldBadge={showTemporaryHoldBadge}
            showConsumablesBadge={showConsumablesBadge}
            consumablesRemainingDays={consumablesRemainingDays}
          />

          <div className={s['patient-info-section']}>
            <UserAvatar
              firstName={firstName}
              lastName={lastName}
              size={40}
              className={s.avatar}
            />
            <Title
              fullName={fullName}
              birthDate={birthDate}
              pcmStatus={pcmStatus}
              isShowEmailTag={isShowEmailTag}
              rpmDoNotCall={rpmDoNotCall}
              ccmDoNotCall={ccmDoNotCall}
              rpmRestrictionType={rpmRestrictionType}
              ccmRestrictionType={ccmRestrictionType}
              showSalesForceIntegration={showSalesForceIntegration}
              sla={sla}
              patientGuid={patientGuid}
            />

            <PatientActionButtons
              patientGuid={patientGuid}
              module={module!}
              onComment={() => setIsCommentsOpen(true)}
              onMore={() => setIsDrawerOpen(true)}
              wrapperClassName={s['action-buttons']}
            />
          </div>
          <div className={s['status-line-section']}>
            <div className={s['status-items']}>
              {rpmStatusObj && (
                <PatientStatusRow
                  label={t('labels.rpmStatus')}
                  value={rpmStatusObj.label}
                  isActiveLabel
                  isActiveValue
                  color={rpmStatusObj.color}
                />
              )}

              {ccmStatusObj && (
                <PatientStatusRow
                  label={t('labels.ccmStatus')}
                  value={ccmStatusObj.label}
                  isActiveLabel
                  isActiveValue
                  color={ccmStatusObj.color}
                />
              )}

              {!isUndefined(pcmStatus) && (
                <PatientStatusRow
                  label={t('labels.pcmStatus')}
                  value={pcmStatus ? t('labels.yes') : t('labels.no')}
                  isActiveLabel
                  isActiveValue
                />
              )}

              {!isUndefined(mobileAppStatus) && (
                <PatientStatusRow
                  label={t('labels.mobileAppStatus')}
                  value={
                    mobileAppStatus ? t('labels.active') : t('labels.inactive')
                  }
                  isActiveLabel
                  isActiveValue
                />
              )}

              {apcmStatusObj && (
                <PatientStatusRow
                  label={t('labels.apcmStatus')}
                  value={apcmStatusObj.label}
                  isActiveLabel
                  isActiveValue
                  color={apcmStatusObj.color}
                />
              )}
            </div>

            {!isUndefined(riskScore) && (
              <div className={s['risk-score-container']}>
                <RiskScoreTag score={riskScore} />
              </div>
            )}
          </div>

          <div className={s.separator} />

          <div className={s['details-section']}>
            <div className={s['details-content']}>
              <div className={s['info-grid']}>
                <div className={s['info-field']}>
                  <span className={s['info-label']}>
                    {t('labels.providerServiceType')}
                  </span>
                  <span className={s['info-value']}>
                    {providerServiceType || 'N/A'}
                  </span>
                </div>

                <div className={s['info-field']}>
                  <span className={s['info-label']}>
                    {t('labels.serviceDeliveryModel')}
                  </span>
                  <span className={s['info-value']}>
                    {serviceDeliveryModel || 'N/A'}
                  </span>
                </div>

                <div className={s['info-field']}>
                  <span className={s['info-label']}>{t('labels.clinic')}</span>
                  <span className={s['info-value']}>{clinicName || 'N/A'}</span>
                </div>

                <div className={s['info-field']}>
                  <span className={s['info-label']}>
                    {t('labels.provider')}
                  </span>
                  <span className={s['info-value']}>{physician || 'N/A'}</span>
                </div>
              </div>

              <div className={s['info-grid']}>
                <div className={s['info-field']}>
                  <span className={s['info-label']}>
                    {t('labels.referralType')}
                  </span>
                  <span className={s['info-value']}>
                    {referralTypes || 'N/A'}
                  </span>
                </div>

                <div className={s['info-field']}>
                  <span className={s['info-label']}>
                    {t('labels.careManagers')}
                  </span>
                  <span className={s['info-value']}>
                    {careAgentName || 'N/A'}
                  </span>
                </div>

                <div className={s['info-field']}>
                  <span className={s['info-label']}>
                    {t('labels.conditions')}
                  </span>
                  <div className={s['conditions-container']}>
                    <span className={s['conditions-value']}>
                      {conditionsText || 'N/A'}
                    </span>
                    {conditions && conditions.length > 0 && (
                      <InformationTooltip
                        content={conditions.map((c) => (
                          <div key={c.icdCode.code}>
                            <p className={s['condition-code']}>
                              {c.icdCode.code}
                            </p>
                            <p className={s['condition-description']}>
                              {c.description}
                            </p>
                          </div>
                        ))}
                        contentClassName={s['conditions-popup-content']}
                        icon={
                          <InfoCircleFilled className={s['info-circle-icon']} />
                        }
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className={s['separator-large']} />

              <div className={s.contact_availability}>
                <div className={s.availability_header}>
                  <div className={s.availability_left}>
                    <button
                      className={s.toggle_button}
                      onClick={() =>
                        setContactAvailabilityExpanded(
                          !contactAvailabilityExpanded,
                        )
                      }
                    >
                      <span className={s.toggle_label}>
                        {t('labels.contactAvailability')}
                      </span>
                      <DownOutlined
                        className={classNames(s.toggle_icon, {
                          [s.expanded]: contactAvailabilityExpanded,
                        })}
                      />
                    </button>
                    <div className={s.availability_info}>
                      {preparedPatientTZ && (
                        <>
                          <span className={s.timezone}>
                            {t('labels.timezone')}: {preparedPatientTZ}
                          </span>
                          <span className={s.pipe_separator}>|</span>
                        </>
                      )}
                      <span className={s.today_availability}>
                        {t('labels.today')}: {todayAvailability}
                      </span>
                    </div>
                  </div>
                  <BaseButton
                    icon={<EditOutlined />}
                    onClick={onEdit}
                    type="secondary"
                    className={s.edit_button}
                    disabled={isReadOnlyRole}
                  />
                </div>

                {contactAvailabilityExpanded && (
                  <div className={s.availability_expanded}>
                    <ContactAvailabilityViewForm
                      patientGuid={patientGuid}
                      withEditBtn={false}
                    />
                  </div>
                )}
              </div>
            </div>

            <InteractiveTimeCard
              ccmClinicTime={ccmClinicTime}
              ccmTotalTime={ccmTotalTime}
              rpmClinicTime={rpmClinicTime}
              rpmTotalTime={rpmTotalTime}
              clinicReviewTimeSeconds={clinicReviewTimeSeconds}
              currentMonth={currentMonth}
              onAdd={onAdd}
              addLoading={addLoading}
            />
          </div>
        </>
      )}

      {module && (
        <GeneralCommentModalNew
          isOpen={isCommentsOpen}
          toggle={setIsCommentsOpen}
          patientGuid={patientGuid}
          loading={commentLoading}
          submit={sendCommentHandler}
          module={module}
          interactionStatus={patientInfo?.interactionStatus}
          tabsProps={{
            showTabs: isCombinedModule,
            disableCcm: isBlockCcmModule,
            disableRpm: isBlockRpmModule,
          }}
        />
      )}

      {module && (
        <PatientActionsDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          patientGuid={patientGuid}
          module={module}
          clinicGuid={clinicGuid}
          emailSubscription={emailSubscription}
          ccmHoldingStatus={holdingStatus?.status !== 'active'}
          disabledCreateInternalAdmissionForms={
            disabledCreateInternalAdmissionForms
          }
        />
      )}

      {isEdit && (
        <MasterModal
          title={t('labels.contactAvailability')}
          rightTitle={contactAvailabilitySwitch}
          isOpen={isEdit}
          toggle={onCancel}
          size="huge"
          modalStyle={{ width: '79.6875rem' }}
          bodyClassName={s.modal_body}
          modalClassName={s.modal}
          footerClassName={s.modal_footer}
          footer={
            <>
              <BaseButton
                key="cancel"
                type="secondary"
                label={t('controls.cancel')}
                onClick={onCancel}
              />
              <BaseButton
                key="save"
                type="primary"
                label={t('controls.save')}
                loading={isSaving}
                onClick={onSave}
              />
            </>
          }
        >
          <ContactAvailabilityUpdateForm
            ref={updateFormRef}
            patientGuid={patientGuid}
            onSaved={onSaved}
            anytime={anytime}
          />
        </MasterModal>
      )}
    </div>
  );
};
export default CombinePatientBanner;
